import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { exchangeSsoTicket } from '../../services/ssoService';
import { takeVerifier, takeRedirect } from '../../utils/ssoPkce';
import LanguagePicker from '../../components/LanguagePicker';
import flexbeeLogo from '../../assets/media/flexbee-no-bg.webp';
import {
  PageWrapper, LanguagePickerWrapper, FormCard, LogoContainer, FormTitle, FormSubtitle, ErrorMessage, GradientLinkButton, BackLink,
} from '../login/styles';

// "#t=<bilet>" ya da "#e=<hata anahtarı>" (spec §5.2 adım 3).
export function readSsoFragment(hash) {
  const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  const ticket = (params.get('t') || '').trim();
  const error = (params.get('e') || '').trim();
  return { ticket: ticket || null, error: /^[a-zA-Z.]{3,80}$/.test(error) ? error : null };
}

export default function SsoCompletePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { adoptSession, loading: authLoading } = useAuth();
  const [view, setView] = useState({ kind: 'entering' });
  const started = useRef(false);
  const alive = useRef(false);
  const adopted = useRef(false);

  useEffect(() => {
    alive.current = true;
    if (!started.current) {
      started.current = true;
      const { ticket, error } = readSsoFragment(window.location.hash);
      // Bilet adreste kalmasın — okunur okunmaz silinir (geçmiş kaydı da değişir: replace).
      if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
      const verifier = takeVerifier();
      if (error || !ticket) {
        setView({ kind: 'error', message: error || 'auth.sso.expired' });
      } else if (!verifier) {
        setView({ kind: 'error', message: 'auth.sso.expired' });
      } else {
        exchangeSsoTicket(ticket, verifier)
          .then((res) => {
            if (!alive.current) return;
            const user = res?.data?.data;
            setView(res?.data?.status && user ? { kind: 'ready', user } : { kind: 'error', message: res?.data?.message || null });
          })
          .catch((err) => {
            if (!alive.current) return;
            setView({ kind: 'error', message: err?.response?.data?.message || null });
          });
      }
    }
    return () => { alive.current = false; };
  }, []);

  // AuthProvider'ın ilk /me denetimi oturumsuz açılışta kullanıcıyı null'a çeker; geç gelirse benimsenen oturumu silerdi.
  useEffect(() => {
    if (view.kind !== 'ready' || authLoading || adopted.current) return;
    adopted.current = true;
    adoptSession(view.user, { email: view.user.email, rememberMe: true });
    history.replace(takeRedirect() || '/');
  }, [view, authLoading, adoptSession, history]);

  if (view.kind === 'error') {
    const fallback = t('login.sso.genericError');
    const message = view.message ? t(`responseErrors.${view.message}`, { defaultValue: fallback }) : fallback;
    return (
      <PageWrapper>
        <LanguagePickerWrapper><LanguagePicker /></LanguagePickerWrapper>
        <FormCard>
          <LogoContainer><img src={flexbeeLogo} alt="Flexbee" /></LogoContainer>
          <FormTitle>{t('login.sso.errorTitle')}</FormTitle>
          <ErrorMessage role="alert" data-role="sso-complete-error"><AlertTriangle size={16} /><span>{message}</span></ErrorMessage>
          <GradientLinkButton as="a" href="/login" data-role="sso-complete-retry">{t('login.sso.retry')}</GradientLinkButton>
          {/* spec §5.5: kip uygunsa parola yolu — zorunlu kipte (auth.sso.required) parola zaten kapalıdır. */}
          {view.message !== 'auth.sso.required' && (
            <BackLink><a href="/login?method=password" data-role="sso-complete-password">{t('login.sso.usePassword')}</a></BackLink>
          )}
        </FormCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <FormCard>
        <LogoContainer><img src={flexbeeLogo} alt="Flexbee" /></LogoContainer>
        <div role="status" aria-live="polite" data-role="sso-complete-entering">
          <Loader2 size={20} />
          <FormTitle>{t('login.sso.completingTitle')}</FormTitle>
          <FormSubtitle>{t('login.sso.completingText')}</FormSubtitle>
        </div>
      </FormCard>
    </PageWrapper>
  );
}
